/**
 * PHASE 8 — single source of truth for the view field of view.
 *
 * The vertical FOV (degrees) drives the projection, the renderer, labels,
 * SeeIt guidance and tap-to-identify. All consumers used to hardcode 60° in
 * four different places; this module is the one authoritative place.
 *
 * The default 60° vertical FOV matches a typical phone camera's vertical
 * angle of view and is a good compromise for sky mode. AR mode may later
 * substitute the camera's real FOV; the interface below keeps that door open
 * without changing existing behavior.
 */

export const DEFAULT_FOV_Y_DEG = 60;

/** Vertical FOV in degrees (always a positive finite number). */
export interface FieldOfView {
  fovYDeg: number;
}

/** Coerce a possibly-invalid override into a usable FOV. */
export function clampFovYDeg(fovYDeg: number, fallback = DEFAULT_FOV_Y_DEG): number {
  if (!Number.isFinite(fovYDeg)) return fallback;
  // Sanity range: a phone lens is roughly 30..120 degrees vertically.
  return Math.min(120, Math.max(15, fovYDeg));
}

/**
 * Resolve the vertical FOV for a frame.
 *
 * @param arMode         true when compositing over the live camera
 * @param cameraFovYDeg  real camera FOV when known (future AR calibration)
 * @param overrideDeg    explicit user/dev override (query param, diagnostics)
 */
export function resolveFovYDeg(arMode: boolean, cameraFovYDeg: number | null, overrideDeg: number | null): number {
  if (overrideDeg !== null) return clampFovYDeg(overrideDeg);
  if (arMode && cameraFovYDeg !== null) return clampFovYDeg(cameraFovYDeg);
  return DEFAULT_FOV_Y_DEG;
}
